/**
 * Timezone <Select> options for the Create/Edit Poll page.
 *
 * The page shows an "Auto (browser)" item whose value is the browser's resolved
 * timezone, followed by the canonical IANA list. The browser timezone is almost
 * always ALSO present in `Intl.supportedValuesOf("timeZone")`, which produced a
 * DUPLICATE `<SelectItem value="...">` — Radix Select keys items by `value`, so
 * React logged "Encountered two children with the same key, `America/New_York`"
 * on every render of the editor (and the duplicated option is a UX wart too).
 *
 * `canonicalTimezoneOptions(browserTimezone, supported)` returns the canonical
 * list with the browser timezone removed, so the page renders it exactly once
 * (as the labeled "Auto (browser)" item).
 *
 * Cross-engine hazard (why we dedup on the RESOLVED zone, not an exact string):
 * `Intl.supportedValuesOf("timeZone")` returns ALIAS forms for several populous
 * zones (e.g. Asia/Calcutta, Asia/Saigon, Europe/Kiev) while
 * `resolvedOptions().timeZone` can return the CANONICAL form (Asia/Kolkata,
 * Asia/Ho_Chi_Minh, Europe/Kyiv) on non-V8 engines (Firefox/WebKit — both in
 * the project parity matrix). An exact-string compare would MISS the alias →
 * the browser tz survives in the list → the duplicate-key error returns for
 * those users. So both sides are normalized through `resolvedOptions().timeZone`
 * and dedup keys on that resolved value.
 */

// Fallback list when the runtime lacks `Intl.supportedValuesOf` (older engines).
export const FALLBACK_TIMEZONES = Object.freeze([
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
]);

/**
 * Resolve a timezone id to its engine-canonical form so alias/canonical pairs
 * (Asia/Calcutta ↔ Asia/Kolkata, …) compare equal. Falls through to the raw id
 * if the runtime lacks `Intl` or the id is invalid (a bogus id must not throw
 * and crash the editor render).
 *
 * @param {string} tz
 * @returns {string} the resolved zone, or `tz` unchanged if it can't be resolved
 */
function resolveZone(tz) {
  if (!tz) return tz;
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: tz }).resolvedOptions()
        .timeZone || tz
    );
  } catch {
    // Invalid/unknown zone id — leave it as-is rather than throwing.
    return tz;
  }
}

/**
 * @param {string} browserTimezone  The resolved browser timezone (the "Auto"
 *   item's value). When falsy, the full list is returned unchanged.
 * @param {string[]} [supported]     The canonical IANA list. Defaults to
 *   `Intl.supportedValuesOf("timeZone")` when available, else FALLBACK_TIMEZONES.
 * @returns {string[]}  The canonical list with `browserTimezone` removed (by
 *   RESOLVED zone, so alias forms are caught too) and any duplicates that
 *   resolve to the same zone collapsed — so every emitted `value` is unique and
 *   none collides with the "Auto" item.
 */
export function canonicalTimezoneOptions(browserTimezone, supported) {
  const base =
    supported ||
    (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : FALLBACK_TIMEZONES);

  // Seed `seen` with the browser tz's RESOLVED zone so any list entry that
  // resolves to the same zone (including an alias form) is skipped.
  const seen = new Set();
  if (browserTimezone) seen.add(resolveZone(browserTimezone));

  const out = [];
  for (const tz of base) {
    if (!tz) continue;
    const zone = resolveZone(tz);
    if (seen.has(zone)) continue; // skip the browser tz + any same-zone dup
    seen.add(zone);
    out.push(tz);
  }
  return out;
}
